import { ComponentProps, ReactNode, useMemo } from 'react';
import {
  Rive,
  useViewModelInstanceTrigger,
  ViewModelInstance,
} from '@rive-app/react-canvas';

import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import IconPlay from 'approot/shared/icons/icon-play';
import IconPause from 'approot/shared/icons/icon-pause';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { DropdownKebab } from 'approot/shared/dropdown/dropdown-kebab';
import { DropdownItemLabel } from 'approot/shared/dropdown/dropdown-item.style';
import { DropdownItemDescriptive } from 'approot/shared/dropdown/dropdown-item';
import {
  ComponentHeightWrapper,
  ComponentInnerWrapper,
} from '../shared/component-height/component-dimensions.style';
import { computePaddingTop } from '../shared/component-height/component-dimension.utils';
import {
  PlayableComponentButton,
  PlayableComponentButtonText,
} from '../shared/playable-component.style';
import RiveAnimation from './rive-animation';
import useRiveViewPlayback from './use-rive-view-playback';
import { ViewProps } from './rive.constants';
import * as Styled from './rive-view.style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivitySheet from '../shared/sheet/activity-sheet';
import RiveLargeStaticPreview from './rive-large-static-preview';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import { PRESENT_MENU_CONTAINER_ID } from 'approot/activity-builder/present/activity-builder-present.constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';

type StateMachineInputs = ReturnType<Rive['stateMachineInputs']>;

// Each render site owns the RiveAnimation it renders, so it also owns the
// playback state for it — see useRiveViewPlayback.
type RivePlayerProps = {
  properties: ViewProps['properties'];
  link: string;
  includeStartOverlay: boolean;
  // Drives both the site's styling and its test ids; the inline site owns the
  // unsuffixed names.
  isExpanded: boolean;
  extraKebabProps?: Partial<ComponentProps<typeof DropdownKebab>>;
  // Rendered after the shared Play/Pause item.
  extraDropdownItems?: ReactNode;
};

// includeStartOverlay: the Activity Builder's edit canvas turns it off. Start is
// a Present-mode affordance, and a full-bleed button over the artwork is the
// front-most surface in the editor once the component is selected (the drag
// handle above it only renders while it is not), so clicking the canvas to
// select a component would play the animation instead. The editor keeps the
// paused animation and its kebab Play item, which is all it had before Start
// existed.
const RiveView = ({
  properties,
  includeStartOverlay = true,
}: ViewProps & { includeStartOverlay?: boolean }) => {
  const {
    isVisible: isExpandModalVisible,
    openModal: openExpandModal,
    closeModal: closeExpandModal,
  } = useSimpleModalState();
  const {
    isVisible: isStaticImageVisible,
    openModal: openStaticImageModal,
    closeModal: closeStaticImageModal,
  } = useSimpleModalState();

  return (
    <ComponentHeightWrapper
      style={{
        paddingTop: `${computePaddingTop(properties.dimensionData)}%`,
      }}
    >
      <ComponentInnerWrapper>
        {isStaticImageVisible && (
          <RiveLargeStaticPreview
            altText={properties.image?.alt || ''}
            imageUrls={properties.imageUrls || []}
            onClose={closeStaticImageModal}
          />
        )}
        {isExpandModalVisible && properties.rive.link && (
          <ActivitySheet
            isVisible
            title={properties.image?.alt || 'Expanded animation'}
            onClose={closeExpandModal}
            data-selector="rive-expanded-modal"
          >
            <RivePlayer
              properties={properties}
              link={properties.rive.link}
              includeStartOverlay={includeStartOverlay}
              isExpanded
            />
          </ActivitySheet>
        )}
        {properties.rive?.link && (
          <RivePlayer
            properties={properties}
            link={properties.rive.link}
            includeStartOverlay={includeStartOverlay}
            isExpanded={false}
            extraKebabProps={{
              placement: 'bottom-end',
              menuContainerElement: PRESENT_MENU_CONTAINER_ID,
              menuWidth: 200,
            }}
            extraDropdownItems={
              <>
                <DropdownItemDescriptive
                  isActive={false}
                  icon={
                    <ActivityBuilderInlineSvg
                      id="icon_expand_arrows"
                      width={20}
                      height={20}
                    />
                  }
                  content={<DropdownItemLabel>Expand</DropdownItemLabel>}
                  onClick={openExpandModal}
                />
                <DropdownItemDescriptive
                  isActive={false}
                  icon={<InlineSvg id="image" width={20} height={20} />}
                  content={
                    <DropdownItemLabel>View Still Image</DropdownItemLabel>
                  }
                  onClick={openStaticImageModal}
                />
              </>
            }
          />
        )}
      </ComponentInnerWrapper>
    </ComponentHeightWrapper>
  );
};

export default RiveView;

const RivePlayer = ({
  properties,
  link,
  includeStartOverlay,
  isExpanded,
  extraKebabProps,
  extraDropdownItems,
}: RivePlayerProps) => {
  const { lang } = useGetInteractiveLessonParams();
  const {
    isPlaying,
    isAwaitingStart,
    shouldAutoPlay,
    viewRef,
    handlePlayPauseToggle,
    handleStartClick,
  } = useRiveViewPlayback({
    riveKey: properties.rive.key,
    includeStartOverlay,
  });

  return (
    <Styled.RiveView
      $isExpanded={isExpanded}
      ref={viewRef}
      tabIndex={-1}
      data-testid={
        isExpanded ? 'rive-view-container-expanded' : 'rive-view-container'
      }
    >
      <RiveAnimation
        key={properties.rive.key}
        link={link}
        width={properties.dimensionData.width * 2}
        height={properties.dimensionData.height * 2}
        altText={properties.image?.alt}
        textRuns={
          properties.rive.enableTextLabels
            ? properties.rive.textLabels || []
            : undefined
        }
        buttonsPosition={properties.rive.buttonsPosition}
        buttons={
          properties.rive.enableButtons
            ? properties.rive.buttons || []
            : undefined
        }
        animationAutoPlay={shouldAutoPlay}
        isCovered={isAwaitingStart}
        language={lang}
        controls={(rive, viewModelInstance, stateMachineInputs) => (
          <>
            {isAwaitingStart && (
              <PlayableComponentButton
                type="button"
                data-testid={
                  isExpanded
                    ? 'rive-start-overlay-expanded'
                    : 'rive-start-overlay'
                }
                aria-label={`Start ${properties.image?.alt || 'animation'}`}
                onClick={() => handleStartClick(rive)}
              >
                <PlayableComponentButtonText>Start</PlayableComponentButtonText>
              </PlayableComponentButton>
            )}
            {/* RiveButtons, not the bare ComponentActions it wraps:
                the overlay is inset-0 at PRESENT_COMPONENT_RESPONSE_Z_INDEX
                (5) and is a sibling of this toolbar under the site's
                wrapper, so at ComponentActions' own z-index (2) Reset
                would be painted over — visible but unclickable, with the
                click starting the animation instead. */}
            <Styled.RiveButtons>
              {properties.rive.enableResetButton && (
                <RiveResetButton
                  viewModelInstance={viewModelInstance}
                  stateMachineInputs={stateMachineInputs}
                />
              )}
              <DropdownKebab
                aria-label="Click to view animation options"
                data-testid={isExpanded ? 'rive-kebab-expanded' : 'rive-kebab'}
                borderRadius={8}
                {...extraKebabProps}
                dropdownContents={
                  <>
                    <DropdownItemDescriptive
                      isActive={false}
                      icon={
                        isPlaying ? (
                          <IconPause width={20} height={20} />
                        ) : (
                          <IconPlay width={20} height={20} />
                        )
                      }
                      content={
                        <DropdownItemLabel>
                          {isPlaying ? 'Pause' : 'Play'}
                        </DropdownItemLabel>
                      }
                      onClick={() => handlePlayPauseToggle(rive)}
                    />
                    {extraDropdownItems}
                  </>
                }
              />
            </Styled.RiveButtons>
          </>
        )}
      />
    </Styled.RiveView>
  );
};

const RiveResetButton = ({
  viewModelInstance,
  stateMachineInputs,
}: {
  viewModelInstance: ViewModelInstance | null;
  stateMachineInputs: StateMachineInputs;
}) => {
  const stateMachineInput = useMemo(
    () => stateMachineInputs.find(input => input.name === 'reset') || null,
    [stateMachineInputs]
  );

  // Get the data binding trigger if data binding is being used for this Rive file
  // Note: an instance is always returned even if the text does not match anything
  const dataBindingTrigger = useViewModelInstanceTrigger(
    'reset',
    viewModelInstance
  );

  return (
    <SecondaryButton
      type="button"
      onClick={() =>
        stateMachineInput
          ? stateMachineInput.fire()
          : dataBindingTrigger
            ? dataBindingTrigger.trigger()
            : undefined
      }
      data-selector="btn-rive-reset"
    >
      Reset
    </SecondaryButton>
  );
};
